import moment from 'moment';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { LOADING } from '../../../redux_store/actionTypes';
import { getTransactionById } from '../../../redux_store/transactions/transaction_thunk';
import { getAmount } from '../../../utils';
import LoadingModal from '../../templates/modals/LoadingModal';
import { User, Wallet } from 'iconoir-react';

const TransferBreakdownPage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { transactions, status, transfer } = useSelector((state) => state.transaction)

  useEffect(() => {
    if (id) {
      dispatch(getTransactionById({ id, criteria: 'TRANSFER' }))
    }
    // eslint-disable-next-line
  }, [id])

  return (
    <div className="space-y-6">
      {status === LOADING && <LoadingModal message={'Loading data...'} />}

      {/* Sender & Receiver Cards */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        {/* Sender Card */}
        <div className='card'>
          <div className='flex items-center gap-2 mb-4'>
            <h3 className='text-lg font-semibold text-surface-800'>Sender Info</h3>
            {transfer.sender ? (
              <span className='badge-success text-xs'>Registered</span>
            ) : (
              <span className='badge-danger text-xs'>Not Registered</span>
            )}
          </div>
          {transfer.sender ? (
            <div className='space-y-3'>
              <div className='flex justify-between py-2 border-b border-surface-100'>
                <span className='text-surface-500'>Full Name</span>
                <span className='font-medium text-surface-800'>
                  {transfer.sender.surname} {transfer.sender.other_names}
                </span>
              </div>
              <div className='flex justify-between py-2 border-b border-surface-100'>
                <span className='text-surface-500'>Role</span>
                <span className='font-medium text-surface-800 capitalize'>{transfer.sender.role}</span>
              </div>
              <div className='flex justify-between py-2 border-b border-surface-100'>
                <span className='text-surface-500'>Email</span>
                <span className='font-medium text-surface-800'>{transfer.sender.email || 'N/A'}</span>
              </div>
              <div className='flex justify-between py-2'>
                <span className='text-surface-500'>Phone</span>
                <span className='font-medium text-surface-800'>{transfer.sender.phone_number}</span>
              </div>
            </div>
          ) : (
            <div className='flex flex-col items-center py-8'>
              <User strokeWidth={1.5} className='w-12 h-12 text-surface-300' />
              <p className='text-surface-400 mt-2'>No sender information</p>
            </div>
          )}
        </div>

        {/* Receiver Card */}
        <div className='card'>
          <div className='flex items-center gap-2 mb-4'>
            <h3 className='text-lg font-semibold text-surface-800'>Receiver Info</h3>
            {transfer.receiver ? (
              <span className='badge-success text-xs'>Registered</span>
            ) : (
              <span className='badge-danger text-xs'>Not Registered</span>
            )}
          </div>
          {transfer.receiver ? (
            <div className='space-y-3'>
              <div className='flex justify-between py-2 border-b border-surface-100'>
                <span className='text-surface-500'>Full Name</span>
                <span className='font-medium text-surface-800'>
                  {transfer.receiver.surname} {transfer.receiver.other_names}
                </span>
              </div>
              <div className='flex justify-between py-2 border-b border-surface-100'>
                <span className='text-surface-500'>Role</span>
                <span className='font-medium text-surface-800 capitalize'>{transfer.receiver.role}</span>
              </div>
              <div className='flex justify-between py-2 border-b border-surface-100'>
                <span className='text-surface-500'>Email</span>
                <span className='font-medium text-surface-800'>{transfer.receiver.email || 'N/A'}</span>
              </div>
              <div className='flex justify-between py-2'>
                <span className='text-surface-500'>Phone</span>
                <span className='font-medium text-surface-800'>{transfer.receiver.phone_number}</span>
              </div>
            </div>
          ) : (
            <div className='flex flex-col items-center py-8'>
              <User strokeWidth={1.5} className='w-12 h-12 text-surface-300' />
              <p className='text-surface-400 mt-2 text-center'>
                Receiver information will be available once they register
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Transactions Table */}
      <div className='card p-0 overflow-hidden'>
        <div className='px-6 py-4 border-b border-surface-100'>
          <h3 className='text-lg font-semibold text-surface-800'>Transaction History</h3>
        </div>
        <table className="table-modern">
          <thead>
            <tr>
              <th>User</th>
              <th>Status</th>
              <th>Amount</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {transactions && transactions.length > 0 ?
              transactions.map((transaction, index) => (
                <tr key={transaction.id || index}>
                  {transaction.user ? (
                    <td>
                      <div className="flex items-center gap-3">
                        <img
                          className="w-10 h-10 rounded-xl object-cover ring-2 ring-surface-100"
                          src={transaction.user.profile_pic}
                          alt=""
                          loading="lazy"
                        />
                        <div>
                          <p className="font-semibold text-surface-800">
                            {transaction.user.surname} {transaction.user.other_names}
                          </p>
                          <p className="text-xs text-surface-400 capitalize">
                            {transaction.user.role}
                          </p>
                        </div>
                      </div>
                    </td>
                  ) : (
                    <td>
                      <span className='text-surface-400 italic'>N/A</span>
                    </td>
                  )}
                  <td>
                    <span className='badge bg-slate-500 text-white'>
                      {transaction.status?.toLowerCase()}
                    </span>
                  </td>
                  <td>
                    <span className='font-semibold text-surface-800'>
                      GH₵ {getAmount(transaction).toFixed(2)}
                    </span>
                  </td>
                  <td className='text-surface-500'>
                    {moment(transaction.createdAt).format('MMM D, YYYY h:mm A')}
                  </td>
                </tr>
              )) :
              <tr>
                <td colSpan={4} className="text-center py-12">
                  <div className='flex flex-col items-center gap-3'>
                    <div className='w-16 h-16 rounded-2xl bg-surface-100 flex items-center justify-center'>
                      <Wallet strokeWidth={1.5} className='w-8 h-8 text-surface-400' />
                    </div>
                    <div>
                      <p className='font-medium text-surface-600'>No transactions</p>
                      <p className='text-sm text-surface-400'>No transaction data available</p>
                    </div>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default TransferBreakdownPage
